import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    CButton,
    CCol,
    CImage,
    CForm,
    CFormInput,
    CInputGroup,
    CInputGroupText,
    CRow,
    CAlert,
    CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLockLocked } from '@coreui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import bgimage from '../../assets/Images/logo_black.png';
import openEyeIcon from '../../assets/Images/eye-open.png';
import closedEyeIcon from '../../assets/Images/eye-closed.png';

// ── Screen states ──
const STATUS = {
    VERIFYING: 'verifying',
    VALID: 'valid',
    INVALID: 'invalid',
    SUCCESS: 'success',
};

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState(STATUS.VERIFYING);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [pwErrors, setPwErrors] = useState({});

    // ── Verify token on mount ──
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setStatus(STATUS.INVALID);
                return;
            }
            try {
                // ── Verify reset URL/token API ──
                await axios.post(`${process.env.REACT_APP_BASE_URL}/user/verify-reset-token`, { token });
                setStatus(STATUS.VALID);
            } catch (err) {
                console.error('Token verification failed:', err);
                setStatus(STATUS.INVALID);
            }
        };
        verifyToken();
    }, [token]);

    const getPwStrength = (pw) => {
        if (!pw) return '';
        let score = 0;
        if (pw.length >= 8) score++;
        if (/[A-Z]/.test(pw)) score++;
        if (/[0-9]/.test(pw)) score++;
        if (/[^A-Za-z0-9]/.test(pw)) score++;
        if (score <= 1) return 'weak';
        if (score <= 3) return 'medium';
        return 'strong';
    };
    const strength = getPwStrength(newPassword);

    const validatePassword = () => {
        const errs = {};
        if (!newPassword) {
            errs.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            errs.newPassword = 'Minimum 8 characters required';
        }
        if (confirmPassword !== newPassword) {
            errs.confirmPassword = 'Passwords do not match';
        }
        setPwErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (form.checkValidity() === false || !validatePassword()) {
            setValidated(true);
            event.stopPropagation();
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/user/reset-password`,
                {
                    token,
                    newPassword,
                }
            );

            if (response.data.success) {
                setStatus(STATUS.SUCCESS);

                setTimeout(() => {
                    navigate("/login");
                }, 2500);
            }
        } catch (error) {
            console.error("Reset Password Error:", error);
            setShowError(true);
            setErrorMessage(
                error?.response?.data?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };
    
    // ── Verifying token ──
    if (status === STATUS.VERIFYING) {
        return (
            <div className="bg-light min-vh-100 d-flex flex-row align-items-center justify-content-center">
                <div className="text-center">
                    <CSpinner color="primary" style={{ width: 40, height: 40 }} />
                    <p style={{ marginTop: 14, color: '#7b8697', fontSize: 14 }}>Verifying your reset link...</p>
                </div>
            </div>
        );
    }

    // ── Invalid / expired token ──
    if (status === STATUS.INVALID) {
        return (
            <div className="bg-light min-vh-100 d-flex flex-row align-items-center justify-content-center">
                <div className="text-center" style={{ maxWidth: 380, padding: 20 }}>
                    <div
                        style={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: '#ea4d4d',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                            color: '#fff',
                            fontSize: 26,
                            fontWeight: 700,
                        }}
                    >
                        !
                    </div>
                    <h4 style={{ fontWeight: 800, color: '#1e2433' }}>Link Invalid or Expired</h4>
                    <p style={{ color: '#7b8697', fontSize: 14, marginTop: 6 }}>
                        This password reset link is no longer valid. Please request a new one.
                    </p>
                    <CButton
                        color="primary"
                        className="btnsign mt-3"
                        onClick={() => navigate('/forgot-password')}
                    >
                        Request New Link
                    </CButton>
                </div>
            </div>
        );
    }

    // ── Success screen ──
    if (status === STATUS.SUCCESS) {
        return (
            <div className="bg-light min-vh-100 d-flex flex-row align-items-center justify-content-center">
                <div className="text-center" style={{ maxWidth: 380, padding: 20 }}>
                    <div
                        style={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: '#00c0f3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                        }}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" width="26" height="26">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
                        </svg>
                    </div>
                    <h4 style={{ fontWeight: 800, color: '#1e2433' }}>Password Updated! 🎉</h4>
                    <p style={{ color: '#7b8697', fontSize: 14, marginTop: 6 }}>
                        Your password has been changed successfully. Redirecting you to login...
                    </p>
                    <CSpinner size="sm" color="primary" className="mt-2" />
                </div>
            </div>
        );
    }

    // ── Valid token → show reset password form ──
    return (
        <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
            <div className="lgnauth">
                <div className="lgnform">
                    <CRow className="justify-content-center">
                        <CCol md={12}>
                            <CForm
                                className="row g-3 needs-validation"
                                noValidate
                                validated={validated}
                                onSubmit={handleSubmit}
                            >
                                <h2 className="lgntxt">Reset Password</h2>
                                <p className='emailheader-txt' style={{ textAlign: "center" }}>
                                    Enter a new password for your account.
                                </p>


                                {showError && (
                                    <CAlert color="warning" dismissible onClose={() => setShowError(false)}>
                                        {errorMessage}
                                    </CAlert>
                                )}

                                {/* ── New Password ── */}
                                <CInputGroup className="mb-2">
                                    <div className="single-input-field">
                                        <CInputGroupText>
                                            <CIcon icon={cilLockLocked} />
                                        </CInputGroupText>
                                        <CFormInput
                                            id="NewPassword"
                                            type={passwordVisible ? 'text' : 'password'}
                                            placeholder="Enter new password"
                                            value={newPassword}
                                            onChange={(e) => {
                                                setNewPassword(e.target.value);
                                                setPwErrors({ ...pwErrors, newPassword: '' });
                                            }}
                                            required
                                            invalid={!!pwErrors.newPassword}
                                        />
                                        <CInputGroupText
                                            className="imgpass-2"
                                            onClick={() => setPasswordVisible((p) => !p)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <CImage
                                                src={passwordVisible ? openEyeIcon : closedEyeIcon}
                                                className="eyeimge"
                                                style={{ width: '20px' }}
                                            />
                                        </CInputGroupText>
                                        {pwErrors.newPassword && (
                                            <div style={{ color: '#ea4d4d', fontSize: 12, marginTop: 4 }}>
                                                {pwErrors.newPassword}
                                            </div>
                                        )}
                                    </div>
                                </CInputGroup>

                                {/* ── Password strength ── */}
                                {newPassword && (
                                    <div style={{ marginTop:0, marginBottom: 4 }}>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            {[0, 1, 2, 3].map((i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        height: 3,
                                                        flex: 1,
                                                        borderRadius: 3,
                                                        background:
                                                            strength === 'weak' && i === 0
                                                                ? '#ea4d4d'
                                                                : strength === 'medium' && i < 2
                                                                    ? '#f5a623'
                                                                    : strength === 'strong'
                                                                        ? '#17c666'
                                                                        : '#e7ecf1',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        <span style={{ fontSize: 10.5, color: "red",fontWeight:"bold" }}>
                                            Strength: {strength.charAt(0).toUpperCase() + strength.slice(1)}
                                        </span>
                                    </div>
                                )}

                                {/* ── Confirm Password ── */}
                                <CInputGroup className="mb-3">
                                    <div className="single-input-field">
                                        <CInputGroupText>
                                            <CIcon icon={cilLockLocked} />
                                        </CInputGroupText>
                                        <CFormInput
                                            id="ConfirmPassword"
                                            type={confirmVisible ? 'text' : 'password'}
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                setPwErrors({ ...pwErrors, confirmPassword: '' });
                                            }}
                                            required
                                            invalid={!!pwErrors.confirmPassword}
                                        />
                                        <CInputGroupText
                                            className="imgpass-2"
                                            onClick={() => setConfirmVisible((p) => !p)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <CImage
                                                src={confirmVisible ? openEyeIcon : closedEyeIcon}
                                                className="eyeimge"
                                                style={{ width: '20px' }}
                                            />
                                        </CInputGroupText>
                                        {pwErrors.confirmPassword && (
                                            <div style={{ color: '#ea4d4d', fontSize: 12, marginTop: 4 }}>
                                                {pwErrors.confirmPassword}
                                            </div>
                                        )}
                                    </div>
                                </CInputGroup>

                                {/* ── Submit ── */}
                                <CButton
                                    color="primary"
                                    className="px-4 btnsign"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Reset Password'}
                                </CButton>
                            </CForm>
                        </CCol>
                    </CRow>
                </div>

                {/* ── Logo / welcome panel ── */}
                <div className="logobg">
                    <h2 className="weltxt">Welcome To</h2>
                    <CImage src={bgimage} height="auto" align="center" width={300} className="mainlgo" />
                    <p className="qqtxt">
                        Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has
                        been the industry's standard dummy text ever since the 1500s, when an unknown printer took a
                        galley of type and scrambled it to make a type specimen book.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;